// Centralized abstraction over storage with type safety,
// error handling, SSR safety, and default values.
// Uses storageBackend.ts for platform-aware persistence
// (Capacitor Preferences on iOS, localStorage on web).

import type { ZodSchema } from 'zod';
import { backendGet, backendSet, backendRemove, isInitialized } from './storageBackend';

export const STORAGE_KEYS = {
  PROGRESS: 'madina_progress',
  ONBOARDING: 'madina-onboarding',
  DARK_MODE: 'madina_dark_mode',
  TTS_ENABLED: 'madina_tts_enabled',
  TTS_SPEED: 'madina_tts_speed',
  TTS_AUTOPLAY: 'madina_tts_autoplay',
  READING_PROGRESS: 'madina-reading-progress',
  READING_TIME: 'madina_reading_time',
  TOPIC_PROGRESS: 'madina_topic_progress',
  FLASHCARD_STATS: 'madina_flashcard_stats',
  FLASHCARD_CONFIG: 'madina_flashcard_config',
  FLASHCARD_MNEMONICS: 'madina_flashcard_mnemonics',
  FLUENCY_STATS: 'madina_fluency_stats',
  TYPING_STATS: 'madina_typing_stats',
  WAZN_STATS: 'madina_wazn_stats',
  WAZN_SETTINGS: 'madina_wazn_settings',
  MORPH_STATS: 'madina_morph_stats',
  MORPH_SETTINGS: 'madina_morph_settings',
  SENTENCE_STATS: 'madina_sentence_stats',
  SPEED_READING_STATS: 'madina_speed_reading_stats',
  SPEED_READING_CONFIG: 'madina_speed_reading_config',
  STUDY_TIME: 'madina_study_time',
  DASHBOARD_SECTIONS: 'madina_dashboard_sections',
  GRAMMAR_RECENT: 'madina_grammar_recent',
  FEATURE_TOUR_COMPLETED: 'madina_feature_tour_completed',
  COMPLETED_PRETESTS: 'completedPreTests',
  CLASSICAL_PROGRESS: 'madina_classical_progress',
  SMART_REVIEW_STATS: 'madina_smart_review_stats',
  TIMED_CHALLENGE_STATS: 'madina_timed_challenge_stats',
  MEMORY_GAME_SCORES: 'madina_memory_game_scores',
  CONJUGATION_SPEED_SCORES: 'madina_conjugation_speed_scores',
  SOUND_ENABLED: 'madina_sound_enabled',
  LEARNING_PATH_PROGRESS: 'madina_learning_path',
  NOTIFICATION_SETTINGS: 'madina_notification_settings',
  NOTIFICATION_DISMISSED: 'madina_notification_dismissed',
  DAILY_PUZZLE: 'madina_daily_puzzle',
  MONTHLY_THEME: 'madina_monthly_theme',
  VOICE_PRACTICE_STATS: 'madina_voice_practice_stats',
  THEME: 'madina_theme',
  AVATAR: 'madina_avatar',
  SESSION_HISTORY: 'madina_session_history',
  DYNAMIC_DIFFICULTY: 'madina_dynamic_difficulty',
  GRAMMAR_SRS: 'madina_grammar_srs',
  GRAMMAR_SRS_STATS: 'madina_grammar_srs_stats',
  INTERLEAVING_STATS: 'madina_interleaving_stats',
  MINIMAL_PAIR_STATS: 'madina_minimal_pair_stats',
  WORD_DEEP_DIVE: 'madina_word_deep_dive',
  DIALOGUE_PROGRESS: 'madina_dialogue_progress',
  WILD_PROGRESS: 'madina_wild_progress',
  MULTIPLAYER_STATS: 'madina_multiplayer_stats',
  MISTAKE_PATTERNS: 'madina_mistake_patterns',
  LISTENING_PROGRESS: 'madina_listening_progress',
  STORY_PROGRESS: 'madina_story_progress',
  STUDY_SESSION: 'madina_study_session',

  JOURNAL_PROGRESS: 'madina_journal',
  FREE_RESPONSE_PROGRESS: 'madina_free_response',
  WRITING_SPRINT: 'madina_writing_sprint',
  LISTENING_LADDER: 'madina_listening_ladder',
  REPEATED_READING: 'madina_repeated_reading',
  MEDIA_PROGRESS: 'madina_media',
  EXPLAIN_IT_PROGRESS: 'madina_explain_it',
  PARAGRAPH_BUILDER_PROGRESS: 'madina_paragraph_builder',
  REGISTER_SHIFT_PROGRESS: 'madina_register_shift',
  TRANSLATION_BENCH_PROGRESS: 'madina_translation_bench',
  STORY_FORGE_PROGRESS: 'madina_story_forge',
  LETTER_CRAFT_PROGRESS: 'madina_letter_craft',
  DEBATE_ARENA_PROGRESS: 'madina_debate_arena',
  DESCRIPTION_LAB_PROGRESS: 'madina_description_lab',
  PARAPHRASE_PRO_PROGRESS: 'madina_paraphrase_pro',
  DIALOGUE_WRITER_PROGRESS: 'madina_dialogue_writer',
  THINK_ALOUD_PROGRESS: 'madina_think_aloud',
  SIMPLIFY_IDEA_PROGRESS: 'madina_simplify_idea',
  THOUGHT_CHAIN_PROGRESS: 'madina_thought_chain',
  SUMMARIZATION_PROGRESS: 'madina_summarization',
  CHALLENGES: 'madina_challenges',
  SYNC_QUEUE: 'madina_sync_queue',
  SYNC_STATE: 'madina_sync_state',
  SYNC_CONFIG: 'madina_sync_config',
  PLACEMENT_TEST: 'madina_placement_test',
  SCENARIO_PROGRESS: 'madina_scenario',
  MICRO_WRITING_PROGRESS: 'madina_micro_writing',
  GRAMMAR_BATTLE: 'madina_grammar_battle',
  LISTENING_LAB: 'madina_listening_lab',
  SPEED_TRAINER: 'madina_speed_trainer',
  SENTENCE_SURGERY: 'madina_sentence_surgery',
  MORPHOLOGY_EXPLORER: 'madina_morphology',
  ROLEPLAY_PROGRESS: 'madina_roleplay',
  ROOT_TREE: 'madina_root_tree',
  GRAMMAR_RELAY: 'madina_grammar_relay',
  CALLIGRAPHY: 'madina_calligraphy',
  GRAMMAR_DETECTIVE: 'madina_grammar_detective',
  DICTATION_CHALLENGE: 'madina_dictation_challenge',
  STRAND_BALANCE: 'madina_strand_balance',
  SESSION_ORCHESTRATOR: 'madina_session',
  DOMAIN_MASTERY: 'madina_domain_mastery',
  SEASONAL_EVENTS: 'madina_seasonal_events',
  STORY_BRANCHES: 'madina_story_progress_branches',
  COMPANION_RELATIONSHIPS: 'companion_relationships',
  COMPANION_SHOWN: 'companion_shown_messages',
  COMPANION_MILESTONES_SHOWN: 'companion_milestones_shown',
  SPACED_REMEDIATION: 'madina_spaced_remediation',
  FRIEND_RIVALRIES: 'madina_friend_rivalries',
  GAUNTLET_HISTORY: 'madina_gauntlet',
  GRAMMAR_DISCOVERY: 'madina_grammar_discovery',
  COLLOCATION_PROGRESS: 'madina_collocation',
  LAST_EXPORT: 'madina_last_export',
  LAST_IMPORT: 'madina_last_import',
  SYNC_TOKEN: 'sync_token',
  ELEVENLABS_API_KEY: 'madina_elevenlabs_key',
  ELEVENLABS_VOICE_ID: 'madina_elevenlabs_voice',
  BROKEN_PLURAL_STATS: 'madina_broken_plural_stats',
  EXPRESSION_LAB: 'madina_expression_lab',

  MASCOT_VISIBLE: 'madina_mascot_visible',
  SMART_NOTIFICATIONS: 'madina_smart_notifications',
  ELO_RATING: 'madina_elo_rating',
} as const;

// ---------------------------------------------------------------------------
// Write listeners — notify subscribers when a key is written
// ---------------------------------------------------------------------------

type StorageWriteListener = (key: string) => void;
const writeListeners: StorageWriteListener[] = [];

// Callback for quota exceeded cleanup (registered by storageRetentionService)
let quotaExceededHandler: (() => void) | null = null;

/**
 * Register a handler that runs when localStorage quota is exceeded.
 * Used by storageRetentionService to wire emergency cleanup without circular imports.
 */
export function onQuotaExceeded(handler: () => void): void {
  quotaExceededHandler = handler;
}

export function onStorageWrite(fn: StorageWriteListener): () => void {
  writeListeners.push(fn);
  return () => {
    const idx = writeListeners.indexOf(fn);
    if (idx >= 0) writeListeners.splice(idx, 1);
  };
}

function isStorageAvailable(): boolean {
  if (isInitialized()) return true;
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function isQuotaExceededError(error: unknown): boolean {
  if (error instanceof DOMException) {
    // Most browsers
    if (error.code === 22 || error.name === 'QuotaExceededError') return true;
    // Firefox
    if (error.code === 1014 || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  }
  return false;
}

/**
 * Read a JSON value from localStorage. Returns defaultValue if the key
 * is missing, the stored string is not valid JSON, or localStorage is
 * unavailable (SSR).
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  if (!isStorageAvailable()) return defaultValue;

  try {
    const raw = backendGet(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * JSON-stringify a value and write it to localStorage.
 * Returns true on success, false on failure (e.g. quota exceeded).
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  if (!isStorageAvailable()) return false;

  try {
    const success = backendSet(key, JSON.stringify(value));
    if (success) {
      writeListeners.forEach(fn => fn(key));
    }
    return success;
  } catch (error) {
    if (isQuotaExceededError(error)) {
      console.warn(`Storage quota exceeded when writing key "${key}" — attempting cleanup`);
      if (quotaExceededHandler) {
        try {
          quotaExceededHandler();
          const retry = backendSet(key, JSON.stringify(value));
          if (retry) writeListeners.forEach(fn => fn(key));
          return retry;
        } catch {
          return false;
        }
      }
    }
    return false;
  }
}

/**
 * Remove an item from localStorage.
 */
export function removeStorageItem(key: string): void {
  if (!isStorageAvailable()) return;
  backendRemove(key);
}

/**
 * Read a raw string value from localStorage (no JSON parsing).
 * Returns defaultValue (or null) if the key is missing or localStorage
 * is unavailable.
 */
export function getStorageString(key: string, defaultValue?: string): string | null {
  if (!isStorageAvailable()) return defaultValue ?? null;

  try {
    const raw = backendGet(key);
    if (raw === null) return defaultValue ?? null;
    return raw;
  } catch {
    return defaultValue ?? null;
  }
}

/**
 * Write a raw string value to localStorage (no JSON stringification).
 * Returns true on success, false on failure.
 */
export function setStorageString(key: string, value: string): boolean {
  if (!isStorageAvailable()) return false;

  try {
    return backendSet(key, value);
  } catch (error) {
    if (isQuotaExceededError(error)) {
      console.warn(`Storage quota exceeded when writing key "${key}"`);
    }
    return false;
  }
}

/**
 * Read a JSON value from localStorage and validate it against a Zod schema.
 * Returns fallback if the key is missing or validation fails.
 *
 * The fallback type F defaults to T (the schema output type) but can be
 * widened (e.g. to `T | null`) so callers can pass `null` as a sentinel
 * for "key not found".
 */
export function getValidatedItem<T, F = T>(
  key: string,
  schema: ZodSchema<T>,
  fallback: F
): T | F {
  const raw = getStorageItem(key, null);
  if (raw === null) return fallback;

  const result = schema.safeParse(raw);
  if (result.success) return result.data;

  console.warn(`[Storage] Invalid data for key "${key}":`, result.error.issues);
  return fallback;
}
